import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Calendar, CheckCircle, ChevronRight, Eye, FileText, LayoutGrid, List, Loader2, Target, Users, XCircle } from 'lucide-react';
import api from '../lib/api';
import SearchField from '../components/SearchField';
import PageShell from '../components/PageShell';
import ViewToggle from '../components/ViewToggle';
import FilterActions from '../components/FilterActions';
import { StandardTableHeader } from '../components/StandardTable';
import EmptyState from '../components/EmptyState';
import { formatDate } from '../utils/dateUtils';
import Pagination from '../components/Pagination';

// Types
interface LobbyistBill {
  id: number;
  row_number?: number;
  lobbyist_id?: string;
  lobbyist_name: string;
  lobbyist_last_name: string;
  lobbyist_first_name: string;
  lobbyist_firm_name: string;
  lobbyist_zip: string;
  primary_lobbyist_id: string;
  annual_lobbyist_registration_id: string;
  bill_number: string;
  bill_information: string;
  client_name: string;
  client_zip: string;
  position: string;
  report_month: string;
  report_due_date: string;
  fiscal_year: string;
  run_date: string;
  created_at: string;
  updated_at: string;
}

interface PaginationState {
  current_page: number;
  total_pages: number;
  total: number;
  limit: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

interface FilterOptions {
  positions: string[];
  fiscal_years: string[];
  report_months: string[];
  firms: string[];
  clients: { name: string; bill_count: number; }[];
}

const LobbyistBillsPage: React.FC = () => {
  const [bills, setBills] = useState<LobbyistBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [pagination, setPagination] = useState<PaginationState>({
    current_page: 1,
    total_pages: 1,
    total: 0,
    limit: 20,
    has_next_page: false,
    has_prev_page: false
  });

  const [filters, setFilters] = useState({
    lobbyist: '',
    bill: '',
    client: '',
    position: '',
    fiscal_year: '',
    report_month: '',
    firm: ''
  });

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    positions: [],
    fiscal_years: [],
    report_months: [],
    firms: [],
    clients: []
  });

  const navigate = useNavigate();

  // Navigate to lobbyist detail page
  const handleRowClick = (bill: LobbyistBill) => {
    // Use lobbyist_id if available, otherwise try to find lobbyist by primary_lobbyist_id
    if (bill.lobbyist_id) {
      navigate(`/lobbyists/${bill.lobbyist_id}`);
    } else if (bill.primary_lobbyist_id) {
      // Navigate to lobbyists page with search for the primary_lobbyist_id
      navigate(`/lobbyists?search=${bill.primary_lobbyist_id}`);
    }
  };

  // Fetch lobbyist bills data
  const fetchBills = async (page = 1) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
        ...(filters.lobbyist && { lobbyist: filters.lobbyist }),
        ...(filters.bill && { bill: filters.bill }),
        ...(filters.client && { client: filters.client }),
        ...(filters.position && { position: filters.position }),
        ...(filters.fiscal_year && { fiscal_year: filters.fiscal_year }),
        ...(filters.report_month && { report_month: filters.report_month }),
        ...(filters.firm && { firm: filters.firm })
      });

      const apiUrl = `/lobbyist-bills?${params.toString()}`;

      const response = await api.get(apiUrl);

      if (response.data.success) {
        setBills(response.data.data.lobbyist_bills);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('LobbyistBillsPage - Error fetching bills:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/lobbyist-bills/filters');
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('LobbyistBillsPage - Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchBills();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search query (300ms delay for text input)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch when debounced search or filters change
  // Filters apply immediately, search is debounced
  useEffect(() => {
    fetchBills(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, filters]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      lobbyist: '',
      bill: '',
      client: '',
      position: '',
      fiscal_year: '',
      report_month: '',
      firm: ''
    });
  };

  const handlePageChange = (newPage: number) => {
    fetchBills(newPage);
  };

  const getPositionColor = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'supporting':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'opposing':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100';
      case 'amending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
      default:
        return 'bg-background-tertiary text-text-primary dark:bg-background-dark-tertiary dark:text-text-dark-primary';
    }
  };

  const getPositionIcon = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'supporting':
        return <CheckCircle className="w-4 h-4" />;
      case 'opposing':
        return <XCircle className="w-4 h-4" />;
      case 'monitoring':
        return <Eye className="w-4 h-4" />;
      case 'amending':
        return <Target className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };


  return (
    <PageShell title="Lobbyist Bills" description="Professional lobbyist bills from Colorado marketplace data">
      <div className="space-y-6 pt-2">

      {/* Search and Filters */}
      <div className="card space-y-6">
        {/* Search Bar */}
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search bills by lobbyist, number, or title..."
          className="w-full"
        />

        {/* Filter Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Position
            </label>
            <select
              className="input"
              value={filters.position}
              onChange={(e) => handleFilterChange('position', e.target.value)}
            >
              <option value="">All positions</option>
              {filterOptions.positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Fiscal Year
            </label>
            <select
              className="input"
              value={filters.fiscal_year}
              onChange={(e) => handleFilterChange('fiscal_year', e.target.value)}
            >
              <option value="">All years</option>
              {filterOptions.fiscal_years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Report Month
            </label>
            <select
              className="input"
              value={filters.report_month}
              onChange={(e) => handleFilterChange('report_month', e.target.value)}
            >
              <option value="">All months</option>
              {filterOptions.report_months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
              Firm
            </label>
            <select
              className="input"
              value={filters.firm}
              onChange={(e) => handleFilterChange('firm', e.target.value)}
            >
              <option value="">All firms</option>
              {filterOptions.firms.slice(0, 50).map((firm) => (
                <option key={firm} value={firm}>
                  {firm}
                </option>
              ))}
            </select>
          </div>
        </div>

        <FilterActions
          onClear={handleClearFilters}
          hasFilters={Boolean(searchQuery) || Object.values(filters).some(f => f)}
        />
      </div>

      {/* View Toggle and Results Count */}
      <div className="flex justify-between items-center">
        {/* View toggle - hidden on mobile (cards only on mobile) */}
        <div className="hidden lg:flex items-center gap-2">
          <ViewToggle
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: 'card', label: 'Card view', icon: LayoutGrid },
              { id: 'table', label: 'List view', icon: List },
            ]}
          />
        </div>
        <div className="flex items-center gap-4">
          {loading && (
            <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {pagination.total} bills found
          </p>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bills.map((bill) => (
                <div
                  key={bill.id}
                  className="card hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/lobbyist-bills/${bill.id}`)}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-text-primary dark:text-text-dark-primary text-lg">
                          {bill.bill_number}
                        </h3>
                        <p className="text-text-secondary dark:text-text-dark-secondary text-sm line-clamp-2">
                          {bill.bill_information}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0 ml-2" />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                        <span className="text-sm text-text-primary dark:text-text-dark-primary">
                          {bill.lobbyist_name}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                        <span className="text-sm text-text-primary dark:text-text-dark-primary">
                          {bill.client_name}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                        <span className="text-sm text-text-primary dark:text-text-dark-primary">
                          {bill.fiscal_year} • {bill.report_month}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPositionColor(bill.position)}`}>
                        {getPositionIcon(bill.position)}
                        {bill.position}
                      </span>
                      
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        Due: {formatDate(bill.report_due_date)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <StandardTableHeader>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Bill
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Lobbyist
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </StandardTableHeader>
                  <tbody className="divide-y divide-border-light dark:divide-border-dark">
                    {bills.map((bill, index) => (
                      <tr 
                        key={bill.id} 
                        className="hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary cursor-pointer transition-colors"
                        onClick={() => handleRowClick(bill)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary dark:text-text-dark-tertiary">
                          {bill.row_number || ((pagination.current_page - 1) * pagination.limit + index + 1)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {bill.bill_number}
                            </div>
                            <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary line-clamp-1">
                              {bill.bill_information}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {bill.lobbyist_name}
                            </div>
                            <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                              {bill.lobbyist_firm_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-text-primary dark:text-text-dark-primary">
                            {bill.client_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPositionColor(bill.position)}`}>
                            {getPositionIcon(bill.position)}
                            {bill.position}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-text-primary dark:text-text-dark-primary">
                              {bill.fiscal_year}
                            </div>
                            <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                              {bill.report_month}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`/lobbyist-bills/${bill.id}`}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {bills.length === 0 && (
            <EmptyState
              icon={FileText}
              title="No lobbyist bills found"
              hint="Try adjusting your search criteria or filters."
            />
          )}

          <Pagination
            page={pagination.current_page}
            pageCount={pagination.total_pages}
            total={pagination.total}
            pageSize={pagination.limit}
            noun="filings"
            onPageChange={handlePageChange}
          />
        </>
      )}
      </div>
    </PageShell>
  );
};

export default LobbyistBillsPage;